import { filterFns, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import sortingUtils from '../../../../utils/sorting.utils';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import ReagentsModal from '../../../Capsules/ReagentsModal';

const CapsuleOverviewTable = ({ configuration, initialConfiguration, isInitialConfigSelected }) => {
    const [sorting, setSorting] = useState([]);

    const columns = [
        {
            id: 'mixture.name',
            accessorFn: row => row.mixture.name,
            header: () => <span>Nom</span>,
            cell: info => <span className={`${info.row.original.mixture.category === 'Effluences' ? 'text-accent' : ''}`}>{info.getValue()}</span>,
            footer: props => props.column.id,
            filterFn: filterFns.includesString,
            sortingFn: sortingUtils.sortText,
            size: 50
        },
        {
            id: 'sensibility',
            accessorFn: row => {
                if (isInitialConfigSelected) {
                    // Configuration initiale : afficher row.sensibility
                    return row.sensibility !== null ? row.sensibility : '-';
                }

                // Pas configuration initiale : afficher initialConfiguration.sensibility
                if (!initialConfiguration || !initialConfiguration.content) return '-';
                const initialRow = initialConfiguration.content.find(
                    initRow => initRow.mixture.id === row.mixture.id
                );
                return initialRow && initialRow.sensibility !== null ? initialRow.sensibility : '-';
            },
            header: () => <span>Sensibilité</span>,
            cell: info => {
                const value = info.getValue();
                const className = value !== '-' ? 'btn btn-sm cursor-pointer btn-primary pointer-events-none' : '';
                return <span className={className}>{value}</span>;
            },
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortNumber,
            filterFn: filterFns.inNumberRange,
            enableHiding: true,
            size: 75
        },
        {
            id: 'validation',
            accessorFn: row => {
                if (isInitialConfigSelected) {
                    // Configuration initiale : afficher "-"
                    return '-';
                }
                // Pas configuration initiale : afficher row.sensibility
                return row.sensibility !== null ? row.sensibility : '-';
            },
            header: () => <span>Validation</span>,
            cell: info => {
                const value = info.getValue();
                if (isInitialConfigSelected) {
                    return <span>-</span>;
                }
                const rowOriginal = info.row.original;
                const initialValue = initialConfiguration?.content?.find(
                    initRow => initRow.mixture.id === rowOriginal.mixture.id
                )?.sensibility;
                let className;
                if (value === '-') {
                    className = '';
                } else if (initialValue === value) {
                    className = 'btn btn-sm cursor-pointer btn-disabled text-base-content/80 pointer-events-none';
                } else {
                    className = 'btn btn-sm cursor-pointer btn-disabled text-base-content/80 pointer-events-none';
                }
                return <span className={className}>{value}</span>;
            },
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortNumber,
            filterFn: filterFns.inNumberRange,
            enableHiding: true,
            size: 75
        },
        {
            id: 'dilution.label',
            accessorFn: row => row?.dilution?.label || row?.mixture?.defaultDilution?.label || '-',
            header: () => <span>Dilution</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'mixture.subCategory',
            accessorFn: row => row.mixture.subCategory,
            header: () => <span >Catégories</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 125

        },
        {
            id: 'mixture.ref',
            accessorFn: row => row.mixture.ref,
            header: () => <span>Référence</span>,
            cell: info => <span >{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'mixture.reagents',
            accessorFn: row => row.mixture.reagents,
            header: () => <span>Réactifs</span>,
            cell: (info) => <ReagentsModal mixtureName={info.row.original.mixture.name} reagents={info.getValue()} description={info.row.original.mixture.description} />,
            footer: props => props.column.id,
            filterFn: filterFns.arrIncludes,
            enableHiding: true,
        },
        {
            id: 'actions',
            accessorFn: row => row.id,
            header: () => <span>Actions</span>,
            cell: info => <ReagentsModal reagents={info.row.original.mixture.reagents} description={info.row.original.mixture.description} mixtureName={info.row.original.mixture.name} showPreview={false} />,
            footer: props => props.column.id,
            enableResizing: false,
            enableGlobalFilter: false,
            size: 50,
        }
    ];

    const tableData = useMemo(() => {
        if (!configuration) return [];
        let returnedData = [...configuration.content];
        // Tri par défaut par catégorie
        returnedData.sort((a, b) => {
            return sortingUtils.sortCategory(a, b, 'mixture.category', false);
        });

        if (sorting.length > 0) {
            const sort = sorting[0];
            const { id, desc } = sort;
            const sortFn = columns.find(column => column.id === id)?.sortingFn;
            if (sortFn && typeof sortFn === 'function') {
                returnedData.sort((a, b) => {
                    return sortFn(a, b, id, desc);
                });
            }
        }

        return returnedData;
    }, [configuration, sorting]);

    const table = useReactTable({
        data: tableData || null,
        columns: columns,
        columnResizeMode: 'onChange',
        columnResizeDirection: 'ltr',
        defaultColumn: {
            size: 75,
            minSize: 20,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        manualFiltering: true,
        enableRowSelection: true,
        getRowId: (row, index) => index
    });

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;


    return (
        <div className="overflow-hidden">
            <table className="table table-md w-full m-auto border-spacing-0">
                <thead>
                    <tr>
                        {headers.map((header) => {
                            const direction = header.column.getIsSorted();
                            const arrow = {
                                asc: <ChevronUpIcon className="w-4 h-4" />,
                                desc: <ChevronDownIcon className="w-4 h-4" />,
                            };
                            const sort_indicator = direction && arrow[direction];

                            return (
                                <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className='relative'>
                                    {header.isPlaceholder ? null : (
                                        <div
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer flex gap-4"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {direction && <span>{sort_indicator}</span>}
                                        </div>
                                    )}
                                    <div onDoubleClick={() => header.column.resetSize()}
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                        className={`resizer ${table.options.columnResizeDirection} ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                                    />
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id} className={``}>
                            {row.getVisibleCells().map((cell, index) => {
                                if (cell.column.id === 'select' && index === 0) return (
                                    <td onClick={(e) => e.stopPropagation()} key={cell.id} style={{ width: cell.column.getSize() }} >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                                return (
                                    <td key={cell.id} style={{ width: cell.column.getSize() }} >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default CapsuleOverviewTable
